import {ButtonIcon as PepsicoDsButtonIcon} from '@pepsico-ds/ui'
import PropTypes from 'prop-types'

/**
 * Icon-only button that renders a PepsicoDsButtonIcon.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.id] - The id of the button.
 * @param {string} props.icon - The name of the icon to display.
 * @param {Function} props.handleButton - The function to handle button click events.
 * @param {string} props.ariaLabel - Accessible label for the button.
 * @param {boolean} [props.isDark=false] - Whether the button renders on a dark background.
 * @param {('large'|'medium'|'small')} [props.size='medium'] - The size of the button.
 * @param {('default'|'naked')} [props.type='naked'] - The visual style of the button.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {string} [props.className] - Additional class names for the button.
 *
 * @returns {JSX.Element} The rendered PepsicoDsButtonIcon component.
 */
const ButtonIcon = ({
  id,
  icon,
  handleButton,
  ariaLabel,
  isDark = false,
  size = 'medium',
  type = 'naked',
  disabled = false,
  className,
}) => {
  return (
    <PepsicoDsButtonIcon
      id={`button-icon` + (id ? `_${id}` : '')}
      data-testid={`button-icon` + (id ? `_${id}` : '')}
      icon={icon}
      onClick={handleButton}
      aria-label={ariaLabel}
      color={isDark ? 'Icon/High-Contrast-Inverse' : 'Icon/Default'}
      size={size}
      type={type}
      disabled={disabled}
      className={className}
    />
  )
}

ButtonIcon.propTypes = {
  id: PropTypes.string,
  icon: PropTypes.string.isRequired,
  handleButton: PropTypes.func,
  ariaLabel: PropTypes.string,
  isDark: PropTypes.bool,
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  type: PropTypes.oneOf(['default', 'naked']),
  disabled: PropTypes.bool,
  className: PropTypes.string,
}

export default ButtonIcon
